import { EntryEditError, audienceText } from '../edits.ts';
import type { Audience } from '../model.ts';
import { parseAudience } from '../queries.ts';
import { javaIsBlank, javaStrip } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlRow } from '../sql.ts';
import { compareDates, type ChronologyDate } from '../calendar/chronologyDate.ts';

/**
 * A realm's Chronology (docs/PARITY-STEP-3-PLAN.md section 4), read and written as the
 * desktop's `ChronologyRepository` and `ChronologyService` do:
 * - timelines form a tree, each level by name without regard to case;
 * - eras and events are listed by start date;
 * - a blank name is "Untitled Timeline", "Untitled Era" or "Untitled Event", and a date's
 *   label is trimmed;
 * - deleting a timeline deletes its sub-timelines, eras and events (the schema's cascades,
 *   with foreign keys on), and deleting an era keeps its events, unfiled.
 *
 * Sort keys are read as text and kept as `bigint`, so a 64-bit key is exact in every SQLite
 * engine (decision 2).
 */

export interface Timeline {
  readonly id: string;
  readonly parentId: string | null;
  readonly name: string;
  readonly description: string;
  readonly created: string;
  readonly modified: string;
}

export interface TimelineNode {
  readonly timeline: Timeline;
  readonly children: readonly TimelineNode[];
}

export interface Era {
  readonly id: string;
  readonly timelineId: string;
  readonly name: string;
  readonly description: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
  readonly created: string;
  readonly modified: string;
}

export interface TimelineEvent {
  readonly id: string;
  readonly timelineId: string;
  readonly eraId: string | null;
  readonly title: string;
  readonly description: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
  readonly entryIds: readonly string[];
  readonly markerIds: readonly string[];
  /** Who may see it (schema migration 25): a copy shared with players leaves out what isn't theirs. */
  readonly audience: Audience;
  readonly created: string;
  readonly modified: string;
}

/** An event in a merged view, with the timeline it belongs to. */
export interface TimelineEventEntry {
  readonly timeline: Timeline;
  readonly event: TimelineEvent;
}

/** A map pin an event can link to (`AtlasService.listAllMarkers`). */
export interface MarkerSummary {
  readonly id: string;
  readonly mapId: string;
  readonly mapName: string;
  readonly label: string;
}

export interface EraInput {
  readonly name: string;
  readonly description: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
}

export interface EventInput extends EraInput {
  readonly eraId: string | null;
  readonly entryIds: readonly string[];
  readonly markerIds: readonly string[];
  /** Reader when made without one; left as it was when updated without one. */
  readonly audience?: Audience;
}

/** What goes with a deletion, for its confirmation (decision 6). */
export interface TimelineDeletionImpact {
  readonly subTimelines: number;
  readonly eras: number;
  readonly events: number;
  /** Links from Story Studio scenes to those events, which go too. */
  readonly sceneLinks: number;
}

export const CHRONOLOGY_NAME_MAX = 200;
export const CHRONOLOGY_DESCRIPTION_MAX = 4000;
export const DATE_LABEL_MAX = 200;

const TIMELINE_COLUMNS = 'id, parent_timeline_id, name, description, created, modified';
const DATE_COLUMNS = `CAST(start_sort_key AS TEXT) AS start_key, start_label, start_uncertain,
  has_end, CAST(end_sort_key AS TEXT) AS end_key, end_label, end_uncertain`;
const ERA_SELECT = `SELECT id, timeline_id, name, description, ${DATE_COLUMNS}, created, modified FROM chronology_eras`;
const EVENT_SELECT = `SELECT id, timeline_id, era_id, title, description, ${DATE_COLUMNS}, audience, created, modified FROM chronology_events`;

// ---- reading ----

/** Every timeline, roots first, each level by name without regard to case. */
export function timelineTree(conn: SqlConnection): TimelineNode[] {
  const all = conn.all(`SELECT ${TIMELINE_COLUMNS} FROM timelines ORDER BY name COLLATE NOCASE`).map(timelineOf);
  const ids = new Set(all.map((t) => t.id));
  const childrenOf = (parentId: string | null): TimelineNode[] =>
    all
      // A parent that's gone (foreign keys off in some older copy) counts as a root, so nothing is lost from view.
      .filter((t) => (parentId === null ? t.parentId === null || !ids.has(t.parentId) : t.parentId === parentId))
      .map((timeline) => ({ timeline, children: childrenOf(timeline.id) }));
  return childrenOf(null);
}

export function getTimeline(conn: SqlConnection, id: string): Timeline | null {
  const row = conn.all(`SELECT ${TIMELINE_COLUMNS} FROM timelines WHERE id = ?`, [id])[0];
  return row ? timelineOf(row) : null;
}

/** A timeline's eras, by start date. */
export function listEras(conn: SqlConnection, timelineId: string): Era[] {
  return conn.all(`${ERA_SELECT} WHERE timeline_id = ? ORDER BY start_sort_key`, [timelineId]).map(eraOf);
}

/** A timeline's own events, by start date, each with its linked entries and pins. */
export function listEvents(conn: SqlConnection, timelineId: string): TimelineEvent[] {
  return conn.all(`${EVENT_SELECT} WHERE timeline_id = ? ORDER BY start_sort_key`, [timelineId]).map((row) => eventOf(conn, row));
}

export function getEvent(conn: SqlConnection, id: string): TimelineEvent | null {
  const row = conn.all(`${EVENT_SELECT} WHERE id = ?`, [id])[0];
  return row ? eventOf(conn, row) : null;
}

/**
 * A timeline's events and all its sub-timelines', by start date
 * (`ChronologyService.eventsIncludingDescendants`): its own first, then each child's in
 * name order, sorted stably, so events on the same day keep that order.
 */
export function eventsIncludingDescendants(conn: SqlConnection, timelineId: string): TimelineEventEntry[] {
  const merged: TimelineEventEntry[] = [];
  const visit = (timeline: Timeline, seen: Set<string>) => {
    if (seen.has(timeline.id)) return;
    seen.add(timeline.id);
    for (const event of listEvents(conn, timeline.id)) merged.push({ timeline, event });
    const children = conn.all(`SELECT ${TIMELINE_COLUMNS} FROM timelines WHERE parent_timeline_id = ? ORDER BY name COLLATE NOCASE`, [timeline.id]).map(timelineOf);
    for (const child of children) visit(child, seen);
  };
  const root = getTimeline(conn, timelineId);
  if (root) visit(root, new Set());
  return merged.sort((a, b) => compareDates(a.event.start, b.event.start));
}

/** Every event in the realm whose range includes the day (`ChronologyService.eventsOnDay`), by start date. */
export function eventsOnDay(conn: SqlConnection, day: bigint): TimelineEventEntry[] {
  const timelines = new Map(conn.all(`SELECT ${TIMELINE_COLUMNS} FROM timelines`).map((row) => [text(row['id']), timelineOf(row)] as const));
  return conn
    .all(`${EVENT_SELECT} ORDER BY start_sort_key`)
    .map((row) => eventOf(conn, row))
    .filter((event) => day >= event.start.sortKey && day <= (event.end ?? event.start).sortKey)
    .flatMap((event) => {
      const timeline = timelines.get(event.timelineId);
      return timeline ? [{ timeline, event }] : [];
    });
}

/** Every event in the realm whose range touches the days `from` to `to`, by start date: a calendar's month or year at once. */
export function eventsBetween(conn: SqlConnection, from: bigint, to: bigint): TimelineEventEntry[] {
  const timelines = new Map(conn.all(`SELECT ${TIMELINE_COLUMNS} FROM timelines`).map((row) => [text(row['id']), timelineOf(row)] as const));
  return conn
    .all(`${EVENT_SELECT} ORDER BY start_sort_key`)
    .map((row) => eventOf(conn, row))
    .filter((event) => event.start.sortKey <= to && (event.end ?? event.start).sortKey >= from)
    .flatMap((event) => {
      const timeline = timelines.get(event.timelineId);
      return timeline ? [{ timeline, event }] : [];
    });
}

/** The day the realm's latest event starts on, or null with none: where a calendar's month view opens. */
export function lastEventDay(conn: SqlConnection): bigint | null {
  const row = conn.all(`${EVENT_SELECT} ORDER BY start_sort_key DESC LIMIT 1`)[0];
  return row ? eventOf(conn, row).start.sortKey : null;
}

/** Every map pin, by map then label (`AtlasRepository.listAllMarkers`). */
export function listMarkers(conn: SqlConnection): MarkerSummary[] {
  return conn
    .all(
      `SELECT mk.id, mk.map_id, m.name AS map_name, mk.label FROM map_markers mk JOIN atlas_maps m ON m.id = mk.map_id
        ORDER BY m.name COLLATE NOCASE, mk.label COLLATE NOCASE`,
    )
    .map((row) => ({ id: text(row['id']), mapId: text(row['map_id']), mapName: text(row['map_name']), label: text(row['label']) }));
}

/** A pin as the desktop names it: "label — map", or "(unlabeled marker) — map". */
export function markerName(marker: MarkerSummary): string {
  return `${javaIsBlank(marker.label) ? '(unlabeled marker)' : marker.label} — ${marker.mapName}`;
}

// ---- writing ----

export function createTimeline(
  conn: SqlConnection,
  name: string,
  description: string,
  parentId: string | null,
  id: string = crypto.randomUUID(),
  now: string = new Date().toISOString(),
): string {
  const clean = { name: cleanName(name, 'Untitled Timeline'), description: cleanDescription(description) };
  transaction(conn, () => {
    if (parentId !== null) mustExist(conn, 'timelines', parentId, 'That timeline no longer exists.');
    conn.run('INSERT INTO timelines (name, description, created, modified, parent_timeline_id, id) VALUES (?, ?, ?, ?, ?, ?)', [
      clean.name,
      clean.description,
      now,
      now,
      parentId,
      id,
    ]);
  });
  return id;
}

export function updateTimeline(conn: SqlConnection, id: string, name: string, description: string, now: string = new Date().toISOString()): void {
  const clean = { name: cleanName(name, 'Untitled Timeline'), description: cleanDescription(description) };
  transaction(conn, () => {
    mustExist(conn, 'timelines', id, 'That timeline no longer exists.');
    conn.run('UPDATE timelines SET name = ?, description = ?, modified = ? WHERE id = ?', [clean.name, clean.description, now, id]);
  });
}

/** What deleting a timeline takes with it: its sub-timelines, every era and event in them, and the scene links to those events. */
export function timelineDeletionImpact(conn: SqlConnection, id: string): TimelineDeletionImpact {
  const ids: string[] = [];
  const queue = [id];
  while (queue.length > 0) {
    const next = queue.shift()!;
    if (ids.includes(next)) continue;
    ids.push(next);
    for (const row of conn.all('SELECT id FROM timelines WHERE parent_timeline_id = ?', [next])) queue.push(text(row['id']));
  }
  const marks = ids.map(() => '?').join(', ');
  return {
    subTimelines: ids.length - 1,
    eras: int(scalar(conn, `SELECT COUNT(*) FROM chronology_eras WHERE timeline_id IN (${marks})`, ids)),
    events: int(scalar(conn, `SELECT COUNT(*) FROM chronology_events WHERE timeline_id IN (${marks})`, ids)),
    sceneLinks: int(
      scalar(conn, `SELECT COUNT(*) FROM story_scene_events WHERE event_id IN (SELECT id FROM chronology_events WHERE timeline_id IN (${marks}))`, ids),
    ),
  };
}

export function deleteTimeline(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'timelines', id, 'That timeline no longer exists.');
    conn.run('DELETE FROM timelines WHERE id = ?', [id]);
  });
}

export function createEra(conn: SqlConnection, timelineId: string, input: EraInput, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = cleanEra(input, 'Untitled Era');
  transaction(conn, () => {
    mustExist(conn, 'timelines', timelineId, 'That timeline no longer exists.');
    conn.run(
      `INSERT INTO chronology_eras (name, description, start_sort_key, start_label, start_uncertain, has_end, end_sort_key, end_label,
         end_uncertain, created, modified, timeline_id, id) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [clean.name, clean.description, ...dateParams(clean.start, clean.end), now, now, timelineId, id],
    );
  });
  return id;
}

export function updateEra(conn: SqlConnection, id: string, input: EraInput, now: string = new Date().toISOString()): void {
  const clean = cleanEra(input, 'Untitled Era');
  transaction(conn, () => {
    mustExist(conn, 'chronology_eras', id, 'That era no longer exists.');
    conn.run(
      `UPDATE chronology_eras SET name = ?, description = ?, start_sort_key = ?, start_label = ?, start_uncertain = ?, has_end = ?,
         end_sort_key = ?, end_label = ?, end_uncertain = ?, modified = ? WHERE id = ?`,
      [clean.name, clean.description, ...dateParams(clean.start, clean.end), now, id],
    );
  });
}

/** Deletes an era; its events stay, unfiled (`era_id` is set to null by the schema). */
export function deleteEra(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'chronology_eras', id, 'That era no longer exists.');
    conn.run('DELETE FROM chronology_eras WHERE id = ?', [id]);
  });
}

export function createEvent(conn: SqlConnection, timelineId: string, input: EventInput, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = cleanEra(input, 'Untitled Event');
  transaction(conn, () => {
    mustExist(conn, 'timelines', timelineId, 'That timeline no longer exists.');
    checkLinks(conn, timelineId, input);
    conn.run(
      `INSERT INTO chronology_events (era_id, title, description, start_sort_key, start_label, start_uncertain, has_end, end_sort_key,
         end_label, end_uncertain, audience, created, modified, timeline_id, id) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [input.eraId, clean.name, clean.description, ...dateParams(clean.start, clean.end), audienceText(input.audience ?? { role: 'READER', customLabel: '' }), now, now, timelineId, id],
    );
    writeLinks(conn, id, input);
  });
  return id;
}

export function updateEvent(conn: SqlConnection, id: string, input: EventInput, now: string = new Date().toISOString()): void {
  const clean = cleanEra(input, 'Untitled Event');
  transaction(conn, () => {
    const timelineId = scalar(conn, 'SELECT timeline_id FROM chronology_events WHERE id = ?', [id]);
    if (timelineId === null) throw new EntryEditError('not_found', 'That event no longer exists.');
    checkLinks(conn, text(timelineId), input);
    conn.run(
      `UPDATE chronology_events SET era_id = ?, title = ?, description = ?, start_sort_key = ?, start_label = ?, start_uncertain = ?,
         has_end = ?, end_sort_key = ?, end_label = ?, end_uncertain = ?, audience = COALESCE(?, audience), modified = ? WHERE id = ?`,
      [input.eraId, clean.name, clean.description, ...dateParams(clean.start, clean.end), input.audience ? audienceText(input.audience) : null, now, id],
    );
    writeLinks(conn, id, input);
  });
}

/** How many Story Studio scenes link to the event, which lose the link if it's deleted. */
export function eventSceneLinks(conn: SqlConnection, id: string): number {
  return int(scalar(conn, 'SELECT COUNT(*) FROM story_scene_events WHERE event_id = ?', [id]));
}

export function deleteEvent(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    mustExist(conn, 'chronology_events', id, 'That event no longer exists.');
    conn.run('DELETE FROM chronology_events WHERE id = ?', [id]);
  });
}

// ---- rows ----

function timelineOf(row: SqlRow): Timeline {
  const parent = row['parent_timeline_id'];
  return {
    id: text(row['id']),
    parentId: parent === null || parent === undefined ? null : text(parent),
    name: text(row['name']),
    description: text(row['description']),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

function datesOf(row: SqlRow): { start: ChronologyDate; end: ChronologyDate | null } {
  const start = { sortKey: BigInt(text(row['start_key']) || '0'), label: text(row['start_label']), uncertain: int(row['start_uncertain']) !== 0 };
  const end = int(row['has_end']) !== 0 ? { sortKey: BigInt(text(row['end_key']) || '0'), label: text(row['end_label']), uncertain: int(row['end_uncertain']) !== 0 } : null;
  return { start, end };
}

function eraOf(row: SqlRow): Era {
  return {
    id: text(row['id']),
    timelineId: text(row['timeline_id']),
    name: text(row['name']),
    description: text(row['description']),
    ...datesOf(row),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

function eventOf(conn: SqlConnection, row: SqlRow): TimelineEvent {
  const id = text(row['id']);
  const era = row['era_id'];
  return {
    id,
    timelineId: text(row['timeline_id']),
    eraId: era === null || era === undefined ? null : text(era),
    title: text(row['title']),
    description: text(row['description']),
    ...datesOf(row),
    entryIds: conn.all('SELECT entry_id FROM chronology_event_entries WHERE event_id = ?', [id]).map((r) => text(r['entry_id'])),
    markerIds: conn.all('SELECT marker_id FROM chronology_event_markers WHERE event_id = ?', [id]).map((r) => text(r['marker_id'])),
    audience: parseAudience(text(row['audience'])),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

// ---- checks ----

/** As the desktop's constructors: a blank name becomes the default, otherwise it's kept as written. */
function cleanName(name: string, fallback: string): string {
  if (name.length > CHRONOLOGY_NAME_MAX) throw new EntryEditError('invalid', `A name can be at most ${CHRONOLOGY_NAME_MAX} characters.`);
  return javaIsBlank(name) ? fallback : name.replace(/[\r\n]+/g, ' ');
}

function cleanDescription(description: string): string {
  if (description.length > CHRONOLOGY_DESCRIPTION_MAX) throw new EntryEditError('invalid', `A description can be at most ${CHRONOLOGY_DESCRIPTION_MAX.toLocaleString('en')} characters.`);
  return description;
}

/** A date's label is trimmed, as `ChronologyDate`'s constructor does. */
function cleanDate(date: ChronologyDate): ChronologyDate {
  if (date.label.length > DATE_LABEL_MAX) throw new EntryEditError('invalid', `A date's label can be at most ${DATE_LABEL_MAX} characters.`);
  if (date.sortKey > 9223372036854775807n || date.sortKey < -9223372036854775808n) throw new EntryEditError('invalid', 'That sort key is too large.');
  return { sortKey: date.sortKey, label: javaStrip(date.label.replace(/[\r\n]+/g, ' ')), uncertain: date.uncertain };
}

function cleanEra(input: EraInput, fallback: string) {
  return {
    name: cleanName(input.name, fallback),
    description: cleanDescription(input.description),
    start: cleanDate(input.start),
    end: input.end === null ? null : cleanDate(input.end),
  };
}

function dateParams(start: ChronologyDate, end: ChronologyDate | null) {
  return [start.sortKey, start.label, start.uncertain ? 1 : 0, end ? 1 : 0, end ? end.sortKey : 0n, end ? end.label : '', end?.uncertain ? 1 : 0];
}

/** An event's era must be one of its own timeline's, and its entries and pins must exist. */
function checkLinks(conn: SqlConnection, timelineId: string, input: EventInput): void {
  if (input.eraId !== null && int(scalar(conn, 'SELECT COUNT(*) FROM chronology_eras WHERE id = ? AND timeline_id = ?', [input.eraId, timelineId])) === 0) {
    throw new EntryEditError('not_found', 'That era is not on this timeline any more.');
  }
  for (const entryId of new Set(input.entryIds)) mustExist(conn, 'lore_entries', entryId, 'One of the linked entries no longer exists.');
  for (const markerId of new Set(input.markerIds)) mustExist(conn, 'map_markers', markerId, 'One of the linked map pins no longer exists.');
}

/** Replaces an event's links, as the desktop's `replaceJoinRows`. */
function writeLinks(conn: SqlConnection, eventId: string, input: EventInput): void {
  conn.run('DELETE FROM chronology_event_entries WHERE event_id = ?', [eventId]);
  for (const entryId of new Set(input.entryIds)) conn.run('INSERT INTO chronology_event_entries (event_id, entry_id) VALUES (?, ?)', [eventId, entryId]);
  conn.run('DELETE FROM chronology_event_markers WHERE event_id = ?', [eventId]);
  for (const markerId of new Set(input.markerIds)) conn.run('INSERT INTO chronology_event_markers (event_id, marker_id) VALUES (?, ?)', [eventId, markerId]);
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
